import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useUpdateNetworkFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/update-network.form-validation';
import { UpdateNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/update-network.model';
import { format, parseISO } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import * as css from './network-update-form-step-1.module.scss';

const NetworkUpdateFormStep1InformationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } =
    useFormContext<Pick<UpdateNetworkModel, 'network' | 'networkId'>>();
  const { networkName, networkDescription } = useUpdateNetworkFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.L} direction="column" flex={1}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="server-06"
        text={t('$.network-update.information')}
        variant={AccentVariantEnum.Primary}
      />
      <Controller
        name="network.name"
        control={control}
        rules={networkName}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-network.name')}
            placeholder={t('delmonicos-shared-sdk.outputs-network.name')}
            required={!!networkName.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="network.description"
        control={control}
        rules={networkDescription}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-network.description')}
            placeholder={t('delmonicos-shared-sdk.outputs-network.description')}
            required={!!networkDescription.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
    </Card>
  );
};

const NetworkUpdateFormStep1InvoicingFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } =
    useFormContext<Pick<UpdateNetworkModel, 'network' | 'networkId'>>();
  const {
    networkHolderFirstName,
    networkHolderLastName,
    networkHolderEmail,
    networkHolderAddress,
    networkHolderAddressCity,
    networkHolderAddressCountryCode,
    networkHolderAddressZipCode,
    networkHolderCompanyName,
    networkHolderVatNumber,
    networkHolderSiret,
  } = useUpdateNetworkFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.L} direction="column">
      <AccentTitle
        as={HeadingEnum.H2}
        icon="receipt-check"
        text={t('$.network-update.invoicing')}
        variant={AccentVariantEnum.Accent1}
      />

      <Stack gap={GapEnum.L} flexWrap="wrap">
        <Controller
          name="network.holder.lastName"
          control={control}
          rules={networkHolderLastName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              required={!!networkHolderLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="network.holder.firstName"
          control={control}
          rules={networkHolderFirstName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              required={!!networkHolderFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>
      <Controller
        name="network.holder.email"
        control={control}
        rules={networkHolderEmail}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.email')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.email')}
            required={!!networkHolderEmail.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="network.holder.companyName"
        control={control}
        rules={networkHolderCompanyName}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.company-name')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.company-name')}
            required={!!networkHolderCompanyName.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="network.holder.siret"
        control={control}
        rules={networkHolderSiret}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.siret')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.siret')}
            required={!!networkHolderSiret.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="network.holder.vatNumber"
        control={control}
        rules={networkHolderVatNumber}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
            required={!!networkHolderVatNumber.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="network.holder.address.address"
        control={control}
        rules={networkHolderAddress}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-address.address')}
            placeholder={t('delmonicos-shared-sdk.outputs-address.address')}
            required={!!networkHolderAddress.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <div className={css.zipCityContainer}>
        <Controller
          name="network.holder.address.zipCode"
          control={control}
          rules={networkHolderAddressZipCode}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              required={!!networkHolderAddressZipCode.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="network.holder.address.city"
          control={control}
          rules={networkHolderAddressCity}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.city')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.city')}
              required={!!networkHolderAddressCity.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </div>
      <Controller
        name="network.holder.address.countryCode"
        control={control}
        rules={networkHolderAddressCountryCode}
        render={({ field, fieldState: { error } }) => (
          <CountryCode
            errorMessage={error?.message}
            fieldValue={field.value}
            fieldOnChange={field.onChange}
          />
        )}
      />
    </Card>
  );
};

const NetworkUpdateFormStep1BankFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } =
    useFormContext<Pick<UpdateNetworkModel, 'network' | 'networkId'>>();
  const {
    networkBillingIban,
    networkBillingBic,
    networkBillingMandateDocumentNumber,
    networkBillingMandateDateOfSignature,
  } = useUpdateNetworkFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.L} direction="column" flex={1}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="credit-card-01"
        text={t('$.network-update.bank')}
        variant={AccentVariantEnum.Accent2}
      />
      <Controller
        name="network.billingDetails.iban"
        control={control}
        rules={networkBillingIban}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.iban')}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-billing-details.iban',
            )}
            required={!!networkBillingIban.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="network.billingDetails.bic"
        control={control}
        rules={networkBillingBic}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            placeholder={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            required={!!networkBillingBic.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Divider />
      <h3>{t('delmonicos-shared-sdk.outputs-billing-details.mandate')}</h3>
      <Controller
        name="network.billingDetails.mandate.documentNumber"
        control={control}
        rules={networkBillingMandateDocumentNumber}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            required={!!networkBillingMandateDocumentNumber.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="network.billingDetails.mandate.dateOfSignature"
        control={control}
        rules={networkBillingMandateDateOfSignature}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            required={!!networkBillingMandateDateOfSignature.required}
            errorMessage={error?.message}
            type="date"
            {...field}
            value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              field.onChange(e.target.value ? parseISO(e.target.value) : null);
            }}
          />
        )}
      />
    </Card>
  );
};

export const NetworkUpdateFormStep1Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.L}>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <NetworkUpdateFormStep1InformationFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <NetworkUpdateFormStep1InvoicingFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <NetworkUpdateFormStep1BankFragment />
      </Stack>
    </Stack>
  );
};
